import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounced } from '@/lib/useDebounced';
import cytoscape, { type Core, type ElementDefinition } from 'cytoscape';
// @ts-expect-error -- no @types package; the runtime export is fine.
import coseBilkent from 'cytoscape-cose-bilkent';

// Cytoscape extensions register globally on first import.
cytoscape.use(coseBilkent);

import { getVizLabelLimit } from '@/lib/preferences';
import { LABEL_MAX_CHARS, compactLabels, truncateLabel } from '@/lib/viz/labels';

// Layout presets:
// - `grid` for placing a single isolated node — predictable spacing when
//   there are no edges for a force layout to optimize against.
// - `cose-bilkent` everywhere else (load + expand): label-aware force layout
//   that renders edges reliably (fcose erratically dropped edge rendering in the
//   browser). Tuned for spread — longer ideal edges + higher repulsion + low
//   gravity — and run GLOBALLY (re-spread) so the graph uses the available canvas
//   rather than crowding one corner. resolveOverlaps() is the no-overlap backstop.
// Cytoscape node-data fields for a base-class palette slot (from the VDB's
// color_index). Empty when the node has no known family — it keeps the neutral
// default fill. Spread into a node's `data` at creation.
function colorData(
  colorIndex: number | null | undefined,
  depth = 0,
): {
  color?: string;
  textColor?: string;
} {
  const color = colorByIndex(colorIndex, depth);
  return color ? { color, textColor: textColorFor(color) } : {};
}

// As `colorData`, but for a base-class node whose colour follows from its URI
// (a base class IS its own family — the permanent slot constant). Used for the
// top-classes / Load-Ontology path, where the row carries no color_index.
function colorDataForUri(uri: string): { color?: string; textColor?: string } {
  const base = baseClassLocalName(uri);
  const color = base ? colorForBaseClass(base) : null;
  return color ? { color, textColor: textColorFor(color) } : {};
}

// Toolbar toggle for a temporary display filter. Active state reads "Show X"
// (click to restore); inactive reads "Hide X".
function FilterToggle({
  active,
  onClick,
  label,
  title,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'rounded-md border px-3 py-1.5 text-sm font-medium',
        active
          ? 'border-tres-action bg-tres-submenu-bg text-tres-action'
          : 'border-input hover:bg-accent',
      )}
    >
      {active ? `Show ${label}` : `Hide ${label}`}
    </button>
  );
}

const GRID_LAYOUT = {
  name: 'grid',
  fit: true,
  padding: 40,
  avoidOverlap: true,
  nodeDimensionsIncludeLabels: true,
  spacingFactor: 1.15,
};

const FORCE_LAYOUT = {
  name: 'cose-bilkent',
  fit: true,
  padding: 60,
  animate: 'end',
  animationDuration: 400,
  nodeDimensionsIncludeLabels: true,
  idealEdgeLength: 180,
  edgeElasticity: 0.45,
  nodeRepulsion: 12000,
  numIter: 2500,
  randomize: true,
  gravity: 0.15,
  gravityRange: 4.0,
};
import {
  fetchVdbClassHierarchy,
  fetchVdbEdges,
  fetchVdbPath,
  fetchVdbSchemaEdges,
  fetchVdbTopClasses,
  saveVizLayout,
  useVdbSearch,
  type VdbEdge,
  type VdbSearchResult,
  type VdbTopClass,
} from '@/lib/useVdb';
import { ALL_GRAPHS } from '@/components/GraphSelector';
import { GraphMultiPicker } from '@/components/GraphMultiPicker';
import {
  hitsWithinScope,
  scopeKey,
  scopeNarrowsHits,
  scopeRequests,
  useGraphScope,
} from '@/lib/useGraphScope';
import { VdbBuildingNotice } from '@/components/VdbBuildingNotice';
import {
  baseClassLocalName,
  colorByIndex,
  colorForBaseClass,
  textColorFor,
} from '@/lib/baseClassColors';
import { useEntity } from '@/lib/useEntity';
import { useApiSession } from '@/lib/useApiSession';
import { useOnDatabaseChange } from '@/lib/useDatabases';
import { EntityView } from './EntityInfoPage';
import { cn } from '@/lib/utils';
import {
  countCrossings,
  countOverlaps,
  defaultView,
  layoutEdges,
  layoutStructure,
  structureSummary,
  viewElements,
  type Placed,
  type StructureView,
} from '@/lib/viz/structure';
import {
  canvasNodeIds,
  canvasSubClassOf,
  hiddenByFilters,
} from '@/lib/viz/displayFilters';
import { classesBelowFirstLevel } from '@/lib/viz/subclassDepth';
import { pathSummary } from '@/lib/viz/pathSummary';
import { tokenHsl, tokenRgb, tokenRgbComma } from '@/lib/tokens';
import { buttonVariants } from '@/components/ui/Button';

// Canvas colours come from the same tokens as the rest of the surface, read
// at render (lib/tokens.ts): Cytoscape takes colour strings, not classes, and
// the values differ between the light and dark themes.
const COLORS = {
  get nodeFill() { return tokenHsl('--card'); },
  get nodeBorder() { return tokenRgb('--tres-action'); },
  get nodeText() { return tokenRgb('--tres-text-primary'); },
  get nodeSelected() { return tokenRgb('--tres-viz-selected'); },
  get nodeSelectedBorder() { return tokenRgb('--tres-viz-selected-border'); },
  get edgeObject() { return tokenRgb('--tres-action'); },
  get edgeHierarchy() { return tokenRgb('--tres-warning'); },
  get edgeAnnotation() { return tokenRgb('--tres-viz-muted'); },
  get edgeMeta() { return tokenRgb('--tres-viz-muted'); },
  get edgeInstance() { return tokenRgb('--tres-viz-muted'); },
  get edgeDatatype() { return tokenRgb('--tres-success'); },
  get edgeDefault() { return tokenRgb('--tres-info'); },
  get pathHighlight() { return tokenRgb('--tres-viz-path'); },
};

function edgeColor(classification: string): string {
  switch (classification) {
    case 'ObjectProperty':
      return COLORS.edgeObject;
    case 'Hierarchy':
      return COLORS.edgeHierarchy;
    case 'Annotation':
      return COLORS.edgeAnnotation;
    case 'Meta':
    case 'MetaProperty':
      return COLORS.edgeMeta;
    case 'Instance':
      return COLORS.edgeInstance;
    case 'DatatypeProperty':
      return COLORS.edgeDatatype;
    default:
      return COLORS.edgeDefault;
  }
}

// sessionStorage so the in-progress search survives nav-tab switches while
// staying isolated per browser tab.
const SEARCH_STORAGE_KEY = 'vizSearch.viz';
const GRAPHS_STORAGE_KEY = 'graphMultiPicker.viz';

/** Fan out a per-graph VDB fetch across the scope and merge by `key`. Each VDB
 *  endpoint takes one graph, so a multi-graph canvas is N requests whose
 *  results are unioned; the all-graphs union stays a single request. */
async function fanOut<T>(
  graphs: string[],
  fetchOne: (graph: string) => Promise<T[]>,
  key: (item: T) => string,
): Promise<T[]> {
  const pages = await Promise.all(scopeRequests(graphs).map(fetchOne));
  const seen = new Set<string>();
  const out: T[] = [];
  for (const page of pages) {
    for (const item of page) {
      const k = key(item);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(item);
    }
  }
  return out;
}

export function VizPage() {
  // Search, expansion, paths and the ontology view are reads a guest holds
  // (Visualize). Only the saved arrangement is personal state, so only it
  // needs a signed-in token.
  const { token, ready } = useApiSession();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<Core | null>(null);
  // Set once the user pans/drags, so a container resize doesn't yank the
  // viewport back from wherever they put it. Cleared by an explicit Fit.
  const userAdjustedViewRef = useRef(false);
  // Debounce handle for persisting a hand-arranged layout.
  const layoutSaveTimer = useRef<number | undefined>(undefined);
  // Latest layout key/token for the save timer, which fires outside React's flow.
  const saveCtx = useRef<{ token: string | undefined; graph: string }>({
    token: undefined,
    graph: '',
  });

  const [search, setSearch] = useState(() => {
    try {
      return window.sessionStorage.getItem(SEARCH_STORAGE_KEY) ?? '';
    } catch {
      return '';
    }
  });
  useEffect(() => {
    try {
      if (search) window.sessionStorage.setItem(SEARCH_STORAGE_KEY, search);
      else window.sessionStorage.removeItem(SEARCH_STORAGE_KEY);
    } catch {
      // Storage unavailable (private mode) — search just won't survive navigation.
    }
  }, [search]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [pathSource, setPathSource] = useState<string | null>(null);
  // Temporary display filters (CLAUDE.local viz #11-12): hide subclass nodes +
  // their subClassOf links; hide datatype-property edges + their literal termini.
  const [hideSubclasses, setHideSubclasses] = useState(false);
  const [hideDatatypes, setHideDatatypes] = useState(false);
  const [expanding, setExpanding] = useState(false);
  // Which structure Load Ontology draws (list 73.2, 73.3). Null until the first
  // load, which picks the view from the ontology's shape.
  const [view, setView] = useState<StructureView | null>(null);
  const [loadingOntology, setLoadingOntology] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Last user-visible action result (e.g., "Loaded 14 top-level classes").
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  // Track placed entities so the search result list can hide them.
  const [placed, setPlaced] = useState<Set<string>>(new Set());
  // Graph scope (a SET). Last-used persists per-page; cold-start defaults to
  // urn:tres:ontology since that's where Load Ontology aims.
  const [graphs, setGraphs] = useGraphScope(GRAPHS_STORAGE_KEY, ['urn:tres:ontology']);
  // A saved arrangement belongs to the scope it was arranged in; the endpoint
  // stores one string per (user, graph), so the set collapses to a stable key.
  const layoutKey = scopeKey(graphs);
  // Persist the hand-arranged layout. Debounced — a drag emits `dragfree` per
  // node, and a user tidying a graph produces a burst of them.
  const scheduleLayoutSave = useCallback(() => {
    window.clearTimeout(layoutSaveTimer.current);
    layoutSaveTimer.current = window.setTimeout(() => {
      const cy = cyRef.current;
      const { token, graph: g } = saveCtx.current;
      if (!cy || !token) return;
      const positions: Record<string, { x: number; y: number }> = {};
      cy.nodes().forEach((n) => {
        const p = n.position();
        positions[n.id()] = { x: p.x, y: p.y };
      });
      // A failed save must not interrupt the drag, but it must not be silent
      // either — the arrangement is user work and would vanish on reload.
      void saveVizLayout(token, g, positions).then(
        (r) => {
          if (!r.ok) setError(`Layout not saved: ${r.message}`);
        },
        (e: unknown) => setError(`Layout not saved: ${e instanceof Error ? e.message : String(e)}`),
      );
    }, 800);
  }, []);

  saveCtx.current = { token, graph: layoutKey };

  // Mount Cytoscape once.
  useEffect(() => {
    if (!containerRef.current) return;
    const cy = cytoscape({
      container: containerRef.current,
      elements: [],
      layout: { name: 'preset' },
      style: [
        {
          selector: 'node',
          style: {
            shape: 'round-rectangle',
            'background-color': COLORS.nodeFill,
            'border-color': COLORS.nodeBorder,
            'border-width': 1.5,
            color: COLORS.nodeText,
            label: nodeLabelText,
            'text-valign': 'center',
            'text-halign': 'center',
            'text-wrap': 'wrap',
            'text-max-width': '140px',
            'font-size': 12,
            padding: '8px',
            width: 'label',
            height: 'label',
            'min-width': '48px',
            'min-height': '26px',
          },
        },
        {
          // A node with undisplayed subclasses gets a dashed border — the visual
          // cue that Expand has more to show here (§7.4 subclass-count marker).
          selector: 'node[?hasHidden]',
          style: { 'border-style': 'dashed', 'border-width': 2 },
        },
        {
          // Base-class family colour from the VDB (data.color), with readable
          // text. Nodes without a known family keep the neutral default above.
          selector: 'node[color]',
          style: {
            'background-color': 'data(color)',
            'border-color': COLORS.nodeFill,
            color: 'data(textColor)',
          },
        },
        {
          selector: 'node.top-level',
          style: {
            'border-width': 3,
            'border-color': COLORS.nodeBorder,
            'font-weight': 'bold',
          },
        },
        {
          // Label economy (§7.4): past the user's node limit, or zoomed far out,
          // a node is a bare box — the graph's shape is the information, and a
          // selected or on-path node still tells the reader what it is.
          selector: 'node.compact',
          style: { label: '' },
        },
        {
          selector: 'node.compact:selected, node.compact.on-path',
          style: { label: nodeLabelText },
        },
        {
          // Selection background is the pale --tres-viz-selected, so force
          // dark text — otherwise a node whose family text colour is white
          // (dark base-class fill) keeps white text and goes unreadable on the
          // pale selected fill.
          selector: 'node:selected',
          style: {
            'background-color': COLORS.nodeSelected,
            'border-color': COLORS.nodeSelectedBorder,
            'border-width': 3.5,
            color: COLORS.nodeText,
          },
        },
        {
          // Find Path result — highlight the path's nodes and edges.
          selector: '.on-path',
          style: {
            'border-color': COLORS.pathHighlight,
            'border-width': 4,
            'line-color': COLORS.pathHighlight,
            'target-arrow-color': COLORS.pathHighlight,
            width: 4,
            'font-weight': 'bold',
            'z-index': 100,
          },
        },
        {
          selector: 'edge',
          style: {
            'curve-style': 'bezier',
            // Several properties between the same two classes fan apart, and
            // each label lies along its own line, so none sits on another.
            'control-point-step-size': 56,
            'text-rotation': 'autorotate',
            'line-color': 'data(color)',
            'target-arrow-color': 'data(color)',
            'target-arrow-shape': 'triangle',
            width: 1.5,
            label: 'data(label)',
            'font-size': 9,
            color: tokenRgbComma('--tres-viz-edge-label'),
            'text-background-color': tokenRgbComma('--tres-viz-edge-label-bg'),
            'text-background-opacity': 0.8,
            'text-background-padding': '2px',
          },
        },
        // Literal terminus nodes (datatype-property ranges) read as leaf values,
        // not entities — a dashed ellipse distinguishes them from class/instance
        // boxes without needing a color family.
        {
          selector: 'node[?isLiteral]',
          style: {
            shape: 'ellipse',
            'border-style': 'dashed',
            'font-style': 'italic',
          },
        },
        // Unspecified endpoint (§7.7 terminator): a property with no declared
        // domain/range. White box, dashed muted border — visibly not a class the
        // user modelled.
        {
          selector: 'node[?isUnspecified]',
          style: {
            'background-color': COLORS.nodeFill,
            'border-color': COLORS.edgeAnnotation,
            'border-style': 'dashed',
            color: COLORS.nodeText,
            'font-style': 'italic',
          },
        },
        // Temporary display-filter class. `display: none` removes the element
        // from render AND layout (and Cytoscape drops edges to a hidden node),
        // so a filtered node/edge doesn't leave a gap or a dangling line.
        {
          selector: '.hidden-by-filter',
          style: { display: 'none' },
        },
      ],
      wheelSensitivity: 0.2,
    });

    // Track selection so the Expand / Clear buttons know which node.
    cy.on('tap', 'node', (e) => {
      setSelectedNodeId(e.target.id());
      // A truncated label is still readable: selecting the node puts the full
      // text in the status line.
      const full = String(e.target.data('label') ?? '');
      if (full.length > LABEL_MAX_CHARS) setStatusMessage(full);
    });
    cy.on('tap', (e) => {
      // Tap on background clears selection.
      if (e.target === cy) setSelectedNodeId(null);
    });

    // Panning or zooming by hand means the user owns the viewport; auto-refit
    // must back off until the next explicit fit or rebuild. A dropped node
    // stays where it was dropped; its neighbours make room (§7.10 Drag).
    cy.on('dragfree', 'node', (e) => {
      userAdjustedViewRef.current = true;
      resolveOverlaps(cy, 18, 80, new Set([e.target.id()]));
      scheduleLayoutSave();
    });

    // Label mode follows node count and zoom; one pass per frame at most.
    let labelFrame = 0;
    const scheduleLabelMode = () => {
      window.cancelAnimationFrame(labelFrame);
      labelFrame = window.requestAnimationFrame(() => applyLabelMode(cy));
    };
    cy.on('add remove zoom', scheduleLabelMode);

    cyRef.current = cy;

    // Without this the canvas can change size (window resize, panel open, device
    // rotation) while Cytoscape keeps rendering at the scale it computed for the
    // old size, stranding the graph in a corner of a larger canvas.
    const container = containerRef.current;
    let resizeTimer: number | undefined;
    const observer = new ResizeObserver(() => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        const active = cyRef.current;
        if (!active) return;
        active.resize();
        if (!userAdjustedViewRef.current) fitToContent(active);
      }, 120);
    });
    if (container) observer.observe(container);

    return () => {
      window.cancelAnimationFrame(labelFrame);
      observer.disconnect();
      window.clearTimeout(resizeTimer);
      cy.destroy();
      cyRef.current = null;
    };
  }, []);

  // Apply the temporary display filters. Re-runs when a toggle flips, and
  // re-applies on every element addition (Load Ontology / Expand add nodes that
  // must respect the current filter state). `display:none` on a node also hides
  // its connected edges, so no dangling lines are left behind.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const apply = () => {
      const hide = hiddenByFilters(cy, { hideSubclasses, hideDatatypes });
      cy.batch(() => {
        // One pass over everything, because the two filters overlap on literal
        // nodes: clearing the class per filter would un-hide what the other hid.
        cy.elements().forEach((el) => {
          if (hide.has(el.id())) el.addClass('hidden-by-filter');
          else el.removeClass('hidden-by-filter');
        });
      });
    };
    apply();
    cy.on('add', apply);
    return () => {
      cy.off('add', apply);
    };
  }, [hideSubclasses, hideDatatypes]);

  // Turning the filter on when the canvas holds nothing below the first level
  // would look broken — the button reports that instead of sitting inert.
  const handleToggleSubclasses = useCallback(() => {
    const next = !hideSubclasses;
    setHideSubclasses(next);
    const cy = cyRef.current;
    if (!next || !cy) return;
    if (classesBelowFirstLevel(canvasNodeIds(cy), canvasSubClassOf(cy)).size === 0) {
      setStatusMessage(
        'Nothing is displayed below the first subclass level, so nothing was hidden. ' +
          'Expand a class to bring deeper levels onto the canvas.',
      );
    }
  }, [hideSubclasses]);

  const handlePlace = useCallback(async (entity: VdbSearchResult) => {
    const cy = cyRef.current;
    if (!cy) return;
    if (cy.getElementById(entity.uri).length > 0) {
      // Already placed — just select it.
      cy.elements().unselect();
      cy.getElementById(entity.uri).select();
      setSelectedNodeId(entity.uri);
      return;
    }
    cy.add({
      group: 'nodes',
      data: {
        id: entity.uri,
        label: entity.label ?? localName(entity.uri),
        ...colorData(entity.color_index),
      },
    });
    cy.elements().unselect();
    cy.getElementById(entity.uri).select();
    setSelectedNodeId(entity.uri);
    setPlaced((prev) => new Set(prev).add(entity.uri));
    setSearch('');

    // Draw any relationships between the newly-placed node and nodes already on
    // the canvas (§7.4 "connect to nodes already displayed"; Rule 9). connectOnly
    // keeps it to edges among the displayed set — placing a node never explodes
    // into its full neighbourhood. Non-fatal on failure.
    if (graphs.length > 0) {
      try {
        const edges = await fanOut(
          graphs,
          (g) => fetchVdbEdges(token, entity.uri, { graph: g, include_types: true }),
          (e) => e.edge_id,
        );
        addEdgesToGraph(cy, edges, setPlaced, { connectOnly: true });
      } catch {
        // ignore — a missing connection is not worth interrupting placement
      }
    }

    // Lots of nodes already, possibly no edges yet — pick layout based
    // on whether the graph has edges.
    const layout = cy.edges().length > 0 ? FORCE_LAYOUT : GRID_LAYOUT;
    void runFullLayout(cy, layout);
    scheduleLayoutSave();
  }, [token, graphs, scheduleLayoutSave]);

  const handleExpand = useCallback(async () => {
    const cy = cyRef.current;
    if (!cy || !selectedNodeId) return;
    if (graphs.length === 0) {
      setError('Select a graph before expanding.');
      return;
    }

    setExpanding(true);
    setError(null);
    setStatusMessage(null);
    try {
      // A graph scope is required — the backend's get_edges rejects global
      // lookups. Mirrors what handleLoadOntology already does for
      // top-classes / class-hierarchy fetches.
      // include_types: show the rdf:type (instance → class) edge — desirable in
      // the data graph. Safe in the ontology graph too: get_edges already filters
      // type-assertions whose object is owl:Class/Thing/Property, so only
      // instance→domain-class edges survive.
      const edges = await fanOut(
        graphs,
        (g) => fetchVdbEdges(token, selectedNodeId, { graph: g, include_types: true }),
        (e) => e.edge_id,
      );
      if (edges.length === 0) {
        setStatusMessage('API returned 0 edges for this node.');
      } else {
        // Snapshot BEFORE adding so we can tell new nodes from existing ones.
        const before = new Set(cy.nodes().map((n) => n.id()));
        addEdgesToGraph(cy, edges, setPlaced);
        const added = cy.nodes().filter((n) => !before.has(n.id()));

        // Expansion must not re-run the whole layout. A full re-spread moves
        // nodes the user has already read — and, now that layouts persist, it
        // discards an arrangement they placed by hand. Instead grow the new
        // neighbours 360° around the expanded node (§7.10) and leave everything
        // already on screen exactly where it is.
        placeRadially(cy, selectedNodeId, added);
        resolveOverlaps(cy, 18, 80, before);
        ensureVisible(cy, added);
        scheduleLayoutSave();
        setStatusMessage(
          `Expanded ${edges.length} edge${edges.length === 1 ? '' : 's'}` +
            (added.length > 0 ? `, ${added.length} new node${added.length === 1 ? '' : 's'}.` : '.'),
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExpanding(false);
    }
  }, [token, selectedNodeId, graphs]);

  // Expand All: cycle every currently-displayed node, fetch its edges, and add
  // them all — new neighbours AND the connections between nodes already on the
  // canvas (§7.4 "Expand All"). The node set is snapshotted first so we expand
  // only what was on screen, not the neighbours we pull in this pass.
  const handleExpandAll = useCallback(async () => {
    const cy = cyRef.current;
    if (!cy) return;
    if (graphs.length === 0) {
      setError('Select a graph before expanding.');
      return;
    }
    const uris = cy.nodes().map((n) => n.id());
    if (uris.length === 0) {
      setStatusMessage('Place or load nodes first, then Expand All.');
      return;
    }

    setExpanding(true);
    setError(null);
    setStatusMessage(null);
    try {
      const results = await Promise.all(
        uris.map((uri) =>
          fanOut(
            graphs,
            (g) => fetchVdbEdges(token, uri, { graph: g, include_types: true }),
            (e) => e.edge_id,
          ).catch(() => [] as VdbEdge[]),
        ),
      );
      for (const edges of results) {
        addEdgesToGraph(cy, edges, setPlaced);
      }
      void runFullLayout(cy, cy.edges().length > 0 ? FORCE_LAYOUT : GRID_LAYOUT);
      scheduleLayoutSave();
      setStatusMessage(`Expanded all — ${cy.nodes().length} nodes, ${cy.edges().length} edges.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExpanding(false);
    }
  }, [token, graphs]);

  // Find Path: first click sets the path source, second click (with a
  // different node selected) computes the shortest semantic path and
  // highlights its nodes + edges, adding any that aren't on the canvas.
  const handleFindPath = useCallback(async () => {
    const cy = cyRef.current;
    if (!cy) return;
    if (!selectedNodeId) {
      setStatusMessage('Select a node, then click Find Path.');
      return;
    }
    if (!pathSource || pathSource === selectedNodeId) {
      setPathSource(selectedNodeId);
      setStatusMessage(`Path start: ${canvasLabel(cy, selectedNodeId)}. Select the other end and press Find Path again.`);
      return;
    }

    const from = pathSource;
    const to = selectedNodeId;
    setExpanding(true);
    setError(null);
    setStatusMessage(null);
    try {
      cy.elements().removeClass('on-path');
      const path = await fetchVdbPath(token, from, to);
      if (!path.found || path.nodes.length === 0) {
        setStatusMessage(`No path joins ${canvasLabel(cy, from)} and ${canvasLabel(cy, to)}.`);
        return;
      }
      for (const [i, uri] of path.nodes.entries()) {
        if (cy.getElementById(uri).length === 0) {
          cy.add({
            group: 'nodes',
            data: { id: uri, label: path.node_labels[i] ?? nodeLabel(uri) },
          });
          setPlaced((prev) => new Set(prev).add(uri));
        }
        cy.getElementById(uri).addClass('on-path');
      }
      scheduleLayoutSave();
      for (const e of path.edges) {
        const id = `path:${e.source}->${e.target}`;
        if (cy.getElementById(id).length === 0) {
          cy.add({
            group: 'edges',
            data: {
              id,
              source: e.source,
              target: e.target,
              label: e.label,
              color: COLORS.pathHighlight,
            },
          });
        }
        cy.getElementById(id).addClass('on-path');
      }
      void runFullLayout(cy, FORCE_LAYOUT);
      setStatusMessage(pathSummary(path, canvasLabel(cy, from), canvasLabel(cy, to)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPathSource(null);
      setExpanding(false);
    }
  }, [token, selectedNodeId, pathSource]);

  // Load Ontology draws one structure on a cleared canvas: the hierarchy (every
  // class, the subclass links) or the relationships (the classes object
  // properties join, and those links), each on a deterministic layered layout
  // (list 73.2, 73.3). Drawn together the two cross each other, so they are
  // never drawn together; the View control switches between them.
  const handleLoadOntology = useCallback(
    async (wanted: StructureView | null) => {
      const cy = cyRef.current;
      if (!cy) {
        setError('The canvas is not ready yet. Try again in a moment.');
        return;
      }
      setLoadingOntology(true);
      setError(null);
      setStatusMessage(null);
      try {
        const [classes, hierarchy, schemaEdges] = await Promise.all([
          fanOut(graphs, (g) => fetchVdbTopClasses(token, g), (c) => c.uri),
          fanOut(graphs, (g) => fetchVdbClassHierarchy(token, g), (e) => e.edge_id),
          fetchVdbSchemaEdges(token),
        ]);
        if (classes.length === 0) {
          setStatusMessage('API returned 0 domain classes — the VDB has no class entities yet.');
          return;
        }
        let chosen = wanted ?? defaultView(classes, schemaEdges);
        let elements = viewElements(chosen, classes, hierarchy, schemaEdges);
        let note = '';
        if (elements.classes.length === 0) {
          note = ' No object property joins two classes here, so the hierarchy is shown.';
          chosen = 'hierarchy';
          elements = viewElements(chosen, classes, hierarchy, schemaEdges);
        }
        setView(chosen);
        cy.elements().remove();
        setSelectedNodeId(null);
        setPathSource(null);
        setPlaced(new Set());
        addTopClassesToGraph(cy, elements.classes, setPlaced);
        addEdgesToGraph(cy, elements.edges, setPlaced);
        const nodes = cy.nodes().map((n) => {
          const box = n.boundingBox({ includeLabels: true });
          return { id: n.id(), width: Math.max(box.w, 48), height: Math.max(box.h, 26) };
        });
        const edges = layoutEdges(chosen, elements.edges);
        applyPositions(cy, await layoutStructure(nodes, edges));
        resolveOverlaps(cy);
        fitToContent(cy);
        const measured: Placed[] = cy.nodes().map((n) => {
          const box = n.boundingBox({ includeLabels: true });
          return { id: n.id(), x: n.position('x'), y: n.position('y'), width: box.w, height: box.h };
        });
        const summary = structureSummary(
          chosen,
          cy.nodes().length,
          cy.edges().length,
          countOverlaps(measured),
          countCrossings(measured, edges),
        );
        // The structure is the drawing, the same every time (73.8); a saved
        // hand arrangement is never laid over it, or the Hierarchy press would
        // put every node back where the last drag left it.
        setStatusMessage(`${summary}${note}`);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoadingOntology(false);
      }
    },
    [token, graphs],
  );

  // The View control: pressing the other view redraws the ontology in it.
  const handleViewChange = useCallback(
    (next: StructureView) => {
      if (next === view) return;
      void handleLoadOntology(next);
    },
    [view, handleLoadOntology],
  );

  const handleClear = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.elements().remove();
    setSelectedNodeId(null);
    setPathSource(null);
    setPlaced(new Set());
    setError(null);
    setStatusMessage(null);
  }, []);
  // The canvas holds one database's nodes; a switch empties it.
  useOnDatabaseChange(handleClear);

  const handleFit = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    // An explicit Fit hands the viewport back to the app.
    userAdjustedViewRef.current = false;
    fitToContent(cy);
  }, []);

  // A saved arrangement must be discardable, or a bad one is permanent. Clears
  // the stored layout and re-runs automatic placement.
  const handleResetLayout = useCallback(async () => {
    const cy = cyRef.current;
    if (!cy) return;
    window.clearTimeout(layoutSaveTimer.current);
    // Re-running placement locally is not the same as clearing the STORED
    // layout: without a successful (or attempted) request the old arrangement
    // comes straight back on reload, so neither a missing token nor a failed
    // call may report success.
    if (!token) {
      setError('Not signed in — the saved layout was not cleared.');
      return;
    }
    const r = await saveVizLayout(token, layoutKey, {});
    if (!r.ok) {
      setError(`Saved layout not cleared: ${r.message}`);
      return;
    }
    userAdjustedViewRef.current = false;
    // A drawn structure comes back as the structure it was (73.8): Reset after a
    // drag redraws the view. A graph built by Expand has no structure to return
    // to and is placed again.
    if (view) {
      await handleLoadOntology(view);
      return;
    }
    void runFullLayout(cy, cy.edges().length > 0 ? FORCE_LAYOUT : GRID_LAYOUT);
    // Honest outcome: only claim a clear when something was stored to clear.
    setStatusMessage(
      r.data?.existed
        ? 'Saved layout cleared — automatic placement restored.'
        : 'No saved layout for this graph — automatic placement re-run.',
    );
  }, [token, layoutKey, view, handleLoadOntology]);

  // §7.10 requires zoom controls alongside Fit; the page had neither in nor out.
  const handleZoom = useCallback((factor: number) => {
    const cy = cyRef.current;
    if (!cy) return;
    userAdjustedViewRef.current = true;
    cy.zoom({
      level: cy.zoom() * factor,
      renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
    });
  }, []);

  // Place-or-select a URI on the canvas — used by the details panel when the
  // user clicks a related entity, so navigation happens in the graph rather
  // than routing away to the Entity page.
  const focusUri = useCallback((uri: string) => {
    const cy = cyRef.current;
    if (!cy) return;
    if (cy.getElementById(uri).length === 0) {
      cy.add({ group: 'nodes', data: { id: uri, label: localName(uri) } });
      setPlaced((prev) => new Set(prev).add(uri));
    }
    cy.elements().unselect();
    cy.getElementById(uri).select();
    setSelectedNodeId(uri);
    void runFullLayout(cy, cy.edges().length > 0 ? FORCE_LAYOUT : GRID_LAYOUT);
  }, []);

  return (
    <div className="w-full px-4 py-6">
      <VdbBuildingNotice className="mb-3" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Visualize</h1>
        <div className="flex flex-wrap items-center gap-2">
          <GraphMultiPicker selected={graphs} onChange={setGraphs} />
          <button
            type="button"
            onClick={() => void handleLoadOntology(view)}
            disabled={!ready || loadingOntology || graphs.length === 0}
            className={cn(
              'rounded-md bg-tres-action px-3 py-1.5 text-sm font-medium text-white',
              'hover:bg-tres-action/90 disabled:opacity-50',
            )}
            title="Draw the ontology's classes from the selected graphs"
          >
            {loadingOntology ? 'Loading…' : 'Load Ontology'}
          </button>
          <div
            className="inline-flex overflow-hidden rounded-md border border-tres-action text-sm font-medium"
            role="group"
            aria-label="View"
          >
            {(['hierarchy', 'relationships'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => handleViewChange(v)}
                disabled={!ready || loadingOntology || graphs.length === 0}
                aria-pressed={view === v}
                title={
                  v === 'hierarchy'
                    ? 'Every class and the subclass links, as a tree'
                    : 'The classes that object properties join, and those links'
                }
                className={cn(
                  'px-3 py-1.5 disabled:opacity-50',
                  view === v ? 'bg-tres-action text-white' : 'text-tres-action hover:bg-tres-submenu-bg',
                )}
              >
                {v === 'hierarchy' ? 'Hierarchy' : 'Relationships'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleExpand}
            disabled={!ready || !selectedNodeId || expanding || graphs.length === 0}
            className={cn(
              'rounded-md border border-tres-action px-3 py-1.5 text-sm font-medium text-tres-action',
              'hover:bg-tres-submenu-bg disabled:opacity-50',
            )}
          >
            {expanding ? 'Expanding…' : 'Expand'}
          </button>
          <button
            type="button"
            onClick={handleExpandAll}
            disabled={!ready || expanding || graphs.length === 0 || placed.size === 0}
            className={cn(
              'rounded-md border border-tres-action px-3 py-1.5 text-sm font-medium text-tres-action',
              'hover:bg-tres-submenu-bg disabled:opacity-50',
            )}
            title="Draw all relationships among the displayed nodes and their direct neighbours"
          >
            Expand All
          </button>
          <button
            type="button"
            onClick={handleFindPath}
            disabled={!ready || !selectedNodeId || expanding}
            className={cn(
              'rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50',
              pathSource
                ? 'border-tres-warning text-tres-warning hover:bg-tres-submenu-bg'
                : 'border-tres-action text-tres-action hover:bg-tres-submenu-bg',
            )}
            title="Shortest semantic path: click to set the start, then select a target and click again"
          >
            {pathSource ? 'Path to…' : 'Find Path'}
          </button>
          <button
            type="button"
            onClick={handleFit}
            className={buttonVariants({ variant: 'secondary', size: 'md' })}
          >
            Fit
          </button>
          {token && (
            <button
              type="button"
              onClick={() => void handleResetLayout()}
              title="Discard the saved arrangement and re-run automatic placement"
              className="rounded-md border border-input px-3 py-1.5 text-sm hover:bg-accent"
            >
              Reset layout
            </button>
          )}
          <button
            type="button"
            onClick={() => handleZoom(1.25)}
            aria-label="Zoom in"
            className="rounded-md border border-input px-3 py-1.5 text-sm hover:bg-accent"
          >
            +
          </button>
          <button
            type="button"
            onClick={() => handleZoom(0.8)}
            aria-label="Zoom out"
            className="rounded-md border border-input px-3 py-1.5 text-sm hover:bg-accent"
          >
            −
          </button>
          <button
            type="button"
            onClick={handleClear}
            className={buttonVariants({ variant: 'secondary', size: 'md' })}
          >
            Clear
          </button>
          <FilterToggle
            active={hideSubclasses}
            onClick={handleToggleSubclasses}
            label="subclasses"
            title="Temporarily hide the subclass levels below the first, and any literal values left with nothing to hang off"
          />
          <FilterToggle
            active={hideDatatypes}
            onClick={() => setHideDatatypes((v) => !v)}
            label="datatypes"
            title="Temporarily hide datatype-property edges and their literal values"
          />
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <SearchPanel
          search={search}
          setSearch={setSearch}
          onPlace={handlePlace}
          placed={placed}
          graphs={graphs}
        />

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="relative rounded-lg border border-border bg-card">
            <div ref={containerRef} className="h-[80vh] w-full" />
            {error && (
              <div className="absolute bottom-3 left-3 right-3 rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">
                {error}
              </div>
            )}
            {!error && statusMessage && (
              <div className="absolute bottom-3 left-3 right-3 rounded-md border border-tres-action/40 bg-tres-submenu-bg p-2 text-xs text-tres-action">
                {statusMessage}
              </div>
            )}
          </div>

          <VizDetailsPanel uri={selectedNodeId} onFocus={focusUri} graphs={graphs} />
        </div>
      </div>

      <Legend />
    </div>
  );
}

const SEARCH_PAGE = 20;

function SearchPanel({
  search,
  setSearch,
  onPlace,
  placed,
  graphs,
}: {
  search: string;
  setSearch: (s: string) => void;
  onPlace: (e: VdbSearchResult) => void;
  placed: Set<string>;
  graphs: string[];
}) {
  const debounced = useDebounced(search, 200);
  // excludeProperties: a property is an edge, never a placeable Viz node.
  // A narrowed scope over-fetches, because the scope filter is applied to the
  // hits here — the search endpoint takes no graph.
  const results = useVdbSearch(debounced, scopeNarrowsHits(graphs) ? 100 : SEARCH_PAGE, true);
  const filtered = useMemo(
    () =>
      hitsWithinScope(results.data ?? [], graphs)
        .filter((r) => !placed.has(r.uri))
        .slice(0, SEARCH_PAGE),
    [results.data, placed, graphs],
  );

  return (
    <div className="relative max-w-xl">
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search labels (3+ chars) to add a node…"
        className="w-full rounded-md border border-input bg-background px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
      {search && (
        <button
          type="button"
          onClick={() => setSearch('')}
          className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'absolute right-2 top-2.5')}
          aria-label="Clear search"
        >
          ×
        </button>
      )}
      {search.length >= 3 && (
        <div className="absolute z-20 mt-1 max-h-80 w-full overflow-y-auto rounded-md border border-border bg-card shadow-lg">
          {results.isLoading && (
            <div className="px-2 py-1.5 text-xs text-tres-text-secondary">Searching…</div>
          )}
          {results.error && (
            <div className="px-2 py-1.5 text-xs text-destructive">
              {(results.error as Error).message}
            </div>
          )}
          {!results.isLoading && filtered.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-tres-text-secondary">No matches.</div>
          )}
          {filtered.map((r) => (
            <button
              type="button"
              key={r.uri}
              onClick={() => onPlace(r)}
              className="block w-full px-2 py-1.5 text-left text-sm hover:bg-accent"
              title={r.uri}
            >
              <div className="truncate font-medium text-tres-text-primary">
                {r.label ?? localName(r.uri)}
              </div>
              <div className="truncate font-mono text-[11px] text-tres-text-secondary">
                {r.entity_type} · {localName(r.uri)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Right-rail details panel — the locked viz "details pane" decision (right,
// persistent, non-resizable). Reuses the Entity page's EntityView so node
// details match the Entity Info surface exactly; in-pane entity-link clicks
// re-center the graph (onFocus) rather than routing away.
function VizDetailsPanel({
  uri,
  onFocus,
  graphs,
}: {
  uri: string | null;
  onFocus: (uri: string) => void;
  graphs: string[];
}) {
  // The entity endpoint takes exactly one graph. A one-graph scope reads that
  // graph; a multi-graph scope reads the union — a superset of the scope, not
  // an arbitrary member of it. The panel is read-only, so a superset misleads
  // no write; the caption below says when it is wider than the selection.
  const wider = graphs.length !== 1;
  const query = useEntity(uri, graphs.length === 1 ? graphs[0]! : ALL_GRAPHS);
  return (
    <aside className="h-[70vh] overflow-y-auto rounded-lg border border-border bg-card p-3">
      {!uri && (
        <div className="flex h-full items-center justify-center px-2 text-center text-sm text-tres-text-secondary">
          Select a node to see its details.
        </div>
      )}
      {uri && wider && (
        <div className="mb-2 text-[11px] text-tres-text-secondary">
          Details shown across all graphs.
        </div>
      )}
      {uri && query.isLoading && (
        <div className="text-sm text-tres-text-secondary">Loading…</div>
      )}
      {uri && query.error && (
        <div className="text-sm text-tres-error">{(query.error as Error).message}</div>
      )}
      {uri && query.data && (
        <EntityView data={query.data} graph={null} onNavigate={onFocus} />
      )}
    </aside>
  );
}

function Legend() {
  const items: Array<[string, string]> = [
    ['ObjectProperty', COLORS.edgeObject],
    ['Hierarchy', COLORS.edgeHierarchy],
    ['DatatypeProperty', COLORS.edgeDatatype],
    ['Annotation / Meta / Instance', COLORS.edgeAnnotation],
  ];
  return (
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-tres-text-secondary">
      {items.map(([label, color]) => (
        <span key={label} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-6" style={{ backgroundColor: color }} />
          {label}
        </span>
      ))}
    </div>
  );
}

// Deterministic collision resolution — the guarantee behind viz layout Rule #1
// ("no node overlaps"; see CLAUDE.local.md graph-visualization rules). Force
// layouts only *reduce* overlap; this pass *eliminates* it: it repeatedly pushes
// any overlapping pair (bounding boxes incl. labels, plus padding) apart along
// the axis of least penetration until none overlap. Run after every layout.
// Ring the newly-revealed neighbours around the node that was expanded, so the
// expansion reads as growing out of it (§7.10) rather than as a reshuffle.
const EXPANSION_RADIUS = 220;

// Put each node where the structure layout placed it.
function applyPositions(cy: Core, placed: Placed[]) {
  for (const p of placed) {
    const node = cy.getElementById(p.id);
    if (node.length > 0) node.position({ x: p.x, y: p.y });
  }
}

function placeRadially(cy: Core, centerId: string, added: cytoscape.NodeCollection) {
  if (added.length === 0) return;
  const center = cy.getElementById(centerId);
  if (center.length === 0) return;
  const c = center.position();
  // Bias the arc away from the canvas centre so growth heads into open space
  // rather than back across the existing graph.
  const away = Math.atan2(c.y, c.x) || 0;
  const radius = EXPANSION_RADIUS + Math.max(0, added.length - 8) * 12;
  added.forEach((n, i) => {
    const angle = away + (2 * Math.PI * i) / added.length;
    n.position({ x: c.x + radius * Math.cos(angle), y: c.y + radius * Math.sin(angle) });
  });
}

// Pan/zoom only if the new nodes landed outside the viewport. Expanding into
// visible space must not move the camera — that is what makes expansion feel
// continuous instead of jumping.
function ensureVisible(cy: Core, added: cytoscape.NodeCollection) {
  if (added.length === 0) return;
  const ext = cy.extent();
  let outside = false;
  added.forEach((n) => {
    const p = n.position();
    if (p.x < ext.x1 || p.x > ext.x2 || p.y < ext.y1 || p.y > ext.y2) outside = true;
  });
  if (outside) fitToContent(cy);
}

// `locked` node ids are held in place — used on expansion so already-read nodes
// (including a hand-placed arrangement) never shift to make room for new ones.
function resolveOverlaps(cy: Core, padding = 18, maxIter = 80, locked?: Set<string>) {
  const nodes = cy.nodes();
  if (nodes.length < 2) return;
  for (let iter = 0; iter < maxIter; iter++) {
    let moved = false;
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i];
        const b = nodes[j];
        if (!a || !b) continue;
        const ba = a.boundingBox({ includeLabels: true });
        const bb = b.boundingBox({ includeLabels: true });
        const ax = (ba.x1 + ba.x2) / 2;
        const ay = (ba.y1 + ba.y2) / 2;
        const bx = (bb.x1 + bb.x2) / 2;
        const by = (bb.y1 + bb.y2) / 2;
        const overlapX = (ba.w + bb.w) / 2 + padding - Math.abs(ax - bx);
        const overlapY = (ba.h + bb.h) / 2 + padding - Math.abs(ay - by);
        if (overlapX <= 0 || overlapY <= 0) continue; // not overlapping
        const aLocked = locked?.has(a.id()) ?? false;
        const bLocked = locked?.has(b.id()) ?? false;
        if (aLocked && bLocked) continue; // nothing may move; leave it
        moved = true;
        // Push apart along the axis of least penetration (minimal disturbance).
        // When one node is locked the other absorbs the whole displacement.
        const aShare = aLocked ? 0 : bLocked ? 1 : 0.5;
        const bShare = bLocked ? 0 : aLocked ? 1 : 0.5;
        if (overlapX < overlapY) {
          const dir = ax <= bx ? -1 : 1;
          a.position('x', a.position('x') + overlapX * aShare * dir);
          b.position('x', b.position('x') - overlapX * bShare * dir);
        } else {
          const dir = ay <= by ? -1 : 1;
          a.position('y', a.position('y') + overlapY * aShare * dir);
          b.position('y', b.position('y') - overlapY * bShare * dir);
        }
      }
    }
    if (!moved) break;
  }
}

// Full-graph (re)layout for load, expand, find-path, and load-from-results.
// fcose distributes + packs components across the canvas; resolveOverlaps is the
// final Rule #1 guarantee (force layouts only reduce, not eliminate, overlap).
// Cap the post-layout zoom so a single node (or a tiny graph) isn't blown up to
// fill the viewport by `fit: true`. Layout fit zooms to pack the content; for one
// node that means an enormous box dominating the canvas. 1.0 means an initial fit
// never magnifies past natural size — above that, small graphs render oversized
// and overflow, which reads as "zoomed in too far" on load.
const MAX_FIT_ZOOM = 1.0;
const FIT_PADDING = 50;

function fitToContent(cy: Core) {
  if (cy.nodes().length === 0) return;
  cy.fit(undefined, FIT_PADDING);
  if (cy.zoom() > MAX_FIT_ZOOM) {
    const center = { x: cy.width() / 2, y: cy.height() / 2 };
    cy.zoom({ level: MAX_FIT_ZOOM, renderedPosition: center });
    cy.center();
  }
}

function runFullLayout(cy: Core, layoutConfig: object): Promise<void> {
  return new Promise((resolve) => {
    const layout = cy.layout(layoutConfig as never);
    layout.one('layoutstop', () => {
      resolveOverlaps(cy);
      fitToContent(cy);
      resolve();
    });
    layout.run();
  });
}

function nodeLabelText(node: cytoscape.NodeSingular): string {
  return truncateLabel(String(node.data('label') ?? ''));
}

function applyLabelMode(cy: Core) {
  const compact = compactLabels(cy.nodes().length, cy.zoom(), getVizLabelLimit());
  cy.batch(() => {
    if (compact) cy.nodes().addClass('compact');
    else cy.nodes().removeClass('compact');
  });
}

// Append edges + their target nodes to the Cytoscape graph. Skips any edges
// whose endpoints are already connected, and adds target nodes only when
// missing. `setPlaced` keeps the search panel in sync.
function addEdgesToGraph(
  cy: Core,
  edges: VdbEdge[],
  setPlaced: React.Dispatch<React.SetStateAction<Set<string>>>,
  opts: { connectOnly?: boolean } = {},
) {
  // connectOnly: draw only edges whose BOTH endpoints are already on the canvas —
  // never pull in new nodes. Used when placing a search result (§7.4 "connect to
  // nodes already displayed") so relationships among the displayed set surface
  // without the placement exploding into its full neighbourhood.
  const connectOnly = opts.connectOnly ?? false;
  const newPlaced = new Set<string>();
  const additions: ElementDefinition[] = [];

  for (const e of edges) {
    if (!e.should_visualize) continue;
    // Key nodes by URI (graph-independent), not the per-graph VDB
    // source_id/target_id — so expand targets merge with the URI-keyed
    // class nodes already on the canvas instead of spawning opaque
    // duplicates. See issue #69.
    // A datatype-property edge terminates at a literal value, not a shared type
    // node. Give each source its OWN terminus (keyed by source + datatype) so
    // every class doesn't collapse onto one xsd:string node (the hairball), and
    // mark it isLiteral so the datatype filter can hide the edge + terminus. (True
    // per-instance-data literals are dropped by the VDB today; this is the
    // schema-level range terminus the canvas actually shows.)
    const isDatatype = e.classification === 'DatatypeProperty';
    const targetId = isDatatype ? `${e.source_uri}|lit|${e.target_uri}` : e.target_uri;

    const sourceExists = cy.getElementById(e.source_uri).length > 0;
    const targetExists = cy.getElementById(targetId).length > 0;

    if (connectOnly && (!sourceExists || !targetExists)) continue;

    // An unspecified endpoint carries no class family — it stays the neutral
    // white box, never a palette colour.
    const sourceUnspecified = isUnspecifiedEndpoint(e.source_uri);
    const targetUnspecified = isUnspecifiedEndpoint(e.target_uri);

    if (!sourceExists) {
      additions.push({
        group: 'nodes',
        data: sourceUnspecified
          ? { id: e.source_uri, label: nodeLabel(e.source_uri), isUnspecified: true }
          : {
              id: e.source_uri,
              label: e.source_label ?? localName(e.source_uri),
              ...colorData(e.source_color_index),
            },
      });
      if (!sourceUnspecified) newPlaced.add(e.source_uri);
    }
    if (!targetExists) {
      additions.push({
        group: 'nodes',
        data: isDatatype
          ? { id: targetId, label: e.target_label ?? localName(e.target_uri), isLiteral: true }
          : targetUnspecified
            ? { id: targetId, label: nodeLabel(e.target_uri), isUnspecified: true }
            : {
                id: targetId,
                label: e.target_label ?? localName(e.target_uri),
                ...colorData(e.target_color_index),
              },
      });
      // Synthetic literal termini and unspecified endpoints aren't searchable
      // entities — keep them out of the placed set (which drives the
      // search-result "already on canvas" hide).
      if (!isDatatype && !targetUnspecified) newPlaced.add(e.target_uri);
    }

    if (cy.getElementById(e.edge_id).length === 0) {
      additions.push({
        group: 'edges',
        data: {
          id: e.edge_id,
          source: e.source_uri,
          target: targetId,
          label: e.label,
          color: edgeColor(e.classification),
          classification: e.classification,
        },
      });
    }
  }

  cy.add(additions);

  if (newPlaced.size > 0) {
    setPlaced((prev) => {
      const next = new Set(prev);
      for (const id of newPlaced) next.add(id);
      return next;
    });
  }
}

function addTopClassesToGraph(
  cy: Core,
  classes: VdbTopClass[],
  setPlaced: React.Dispatch<React.SetStateAction<Set<string>>>,
) {
  const newPlaced = new Set<string>();
  const additions: ElementDefinition[] = [];
  for (const c of classes) {
    if (cy.getElementById(c.uri).length > 0) continue;
    // §7.4: a node carrying undisplayed subclasses shows its direct-subclass
    // count. Without it the opening view hides classes with no cue as to where
    // they went, and Expand becomes guesswork.
    const hiddenChildren = c.subclass_count > 0;
    const node: ElementDefinition = {
      group: 'nodes',
      data: {
        id: c.uri,
        label: hiddenChildren
          ? `${c.label || localName(c.uri)}  (${c.subclass_count})`
          : c.label || localName(c.uri),
        subclassCount: c.subclass_count,
        hasHidden: hiddenChildren,
        // Marker used by the stylesheet for stronger visual weight on roots.
        topLevel: c.is_top_level,
        // Prefer the inherited family slot (a subclass gets its top-class
        // ancestor's color), shaded progressively lighter by hierarchy depth;
        // fall back to base-class-by-URI for the 11 base classes.
        ...(c.color_index != null
          ? colorData(c.color_index, c.hierarchy_depth)
          : colorDataForUri(c.uri)),
      },
    };
    if (c.is_top_level) node.classes = 'top-level';
    additions.push(node);
    newPlaced.add(c.uri);
  }
  cy.add(additions);
  if (newPlaced.size > 0) {
    setPlaced((prev) => {
      const next = new Set(prev);
      for (const id of newPlaced) next.add(id);
      return next;
    });
  }
}

function localName(uri: string): string {
  if (!uri) return '';
  const hashIdx = uri.lastIndexOf('#');
  const slashIdx = uri.lastIndexOf('/');
  const idx = Math.max(hashIdx, slashIdx);
  if (idx > 0 && idx < uri.length - 1) {
    return uri.slice(idx + 1);
  }
  return uri;
}

// A property with no declared domain (or range) has no class at that end, so the
// VDB mints a per-property `urn:terminator:missing-…` placeholder to terminate
// the edge. That URN has no # or / for localName to cut on, so it rendered in
// full across the canvas — and it over-claims: an undeclared domain often means
// the predicate applies to any resource (the `:label` shape), not that something
// is missing. Render the endpoint for what it is.
const TERMINATOR_PREFIX = 'urn:terminator:';

function isUnspecifiedEndpoint(uri: string): boolean {
  return uri.startsWith(TERMINATOR_PREFIX);
}

function nodeLabel(uri: string): string {
  return isUnspecifiedEndpoint(uri) ? 'Any' : localName(uri);
}

/** The name a node carries on the canvas, for messages about it. */
function canvasLabel(cy: Core, id: string): string {
  const label: unknown = cy.getElementById(id).data('label');
  return typeof label === 'string' && label.trim() ? label : nodeLabel(id);
}
